import React, { useMemo, useState } from "react";
import {
  Button,
  CustomInput,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  UncontrolledDropdown,
} from "reactstrap";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";
import { AddCommaNumber } from "../../../helpers/Utils";
import { Pagination } from "../general";

const fileType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

const safeNumber = (value) => {
  const parsed = Number(String(value || 0).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d");

const formatMoney = (value) => `${AddCommaNumber(Math.round(value || 0))} đ`;

const isPending = (status) => normalizeText(status).includes("cho");
const isApproved = (status) => normalizeText(status).includes("da xet duyet");
const isRejected = (status) => normalizeText(status).includes("tu choi");

const getReceiptCode = (item) => {
  if (item?.code) {
    if (String(item.code).startsWith("PN")) {
      return item.code;
    }
    // Nếu mã code là chuỗi barcode (toàn số 8-14 chữ số), format sang mã phiếu chuẩn
    if (/^\d{8,14}$/.test(String(item.code).trim())) {
      const datePart = (item.at || item.createdAt || "").replace(/\D/g, "").slice(0, 8);
      return datePart ? `PN${datePart}01` : `PN${String(item?.id || "").slice(0, 8).toUpperCase()}`;
    }
    return item.code;
  }
  return `PN${String(item?.id || "").padStart(6, "0")}`;
};

const getCreatorName = (item) =>
  item?.user?.fullname || item?.user?.username || item?.user || "—";

const getStatusClass = (status) => {
  if (isApproved(status)) return "approved";
  if (isPending(status)) return "pending";
  if (isRejected(status)) return "rejected";
  return "draft";
};

const exportReceipts = (data) => {
  const rows = data.map((item, index) => ({
    STT: index + 1,
    "Mã phiếu": getReceiptCode(item),
    "Ngày nhập": item.at,
    "Mặt hàng": item.stock,
    "Mã hàng": item.stock_sku || item.stock_barcode || "—",
    "Đơn vị": item.unit,
    "Số lượng": safeNumber(item.quantity),
    "Đơn giá": safeNumber(item.price),
    "Thành tiền": safeNumber(item.quantity) * safeNumber(item.price),
    "Nhà cung cấp": item.name || "—",
    "Số điện thoại": item.phone,
    "Địa chỉ": item.address,
    "Trạng thái": item.status,
  }));
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = {
    Sheets: { "Phiếu nhập kho": worksheet },
    SheetNames: ["Phiếu nhập kho"],
  };
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  FileSaver.saveAs(
    new Blob([buffer], { type: fileType }),
    "Danh-sach-phieu-nhap-kho.xlsx",
  );
};

const SummaryCard = ({ label, value, note, icon, tone }) => (
  <div className="warehouse-import-summary-card">
    <div>
      <span className="warehouse-import-summary-label">{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </div>
    <span className={`warehouse-import-summary-icon tone-${tone}`}>
      <i className={icon} />
    </span>
  </div>
);

const StatusPill = ({ status }) => (
  <span className={`warehouse-import-status status-${getStatusClass(status)}`}>
    {status || "Nháp"}
  </span>
);

const EmptyDetail = () => (
  <div className="warehouse-import-detail-empty">
    <span>
      <i className="iconsminds-box-with-folders" />
    </span>
    <strong>Chọn một phiếu nhập</strong>
    <p>Thông tin chi tiết của phiếu sẽ hiển thị tại đây.</p>
  </div>
);

const WarehouseImportModernDashboard = ({
  data,
  dataAll,
  page,
  filter,
  selectedItems,
  selectedItem,
  canCreate,
  canUpdate,
  canDelete,
  onAdd,
  onCameraImport,
  onEdit,
  onDelete,
  onApprove,
  onReject,
  onSelect,
  onToggleSelected,
  onDeleteSelected,
  onSearch,
  onFilter,
  onChangePage,
  onChangePageSize,
}) => {
  const [activeTab, setActiveTab] = useState("info");
  const [statusFilter, setStatusFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const allRows = useMemo(
    () => (Array.isArray(dataAll) ? dataAll : []),
    [dataAll],
  );
  const rows = Array.isArray(data) ? data : [];

  const summary = useMemo(() => {
    return allRows.reduce(
      (result, item) => {
        if (isPending(item.status)) result.pending += 1;
        if (isApproved(item.status)) {
          result.approved += 1;
          result.totalValue += safeNumber(item.quantity) * safeNumber(item.price);
        }
        return result;
      },
      { totalValue: 0, pending: 0, approved: 0 },
    );
  }, [allRows]);

  const unitOptions = useMemo(
    () => [...new Set(allRows.map((item) => item.unit).filter(Boolean))],
    [allRows],
  );
  const statusOptions = useMemo(
    () => [...new Set(allRows.map((item) => item.status).filter(Boolean))],
    [allRows],
  );
  const hasActiveFilter = Boolean(
    statusFilter || unitFilter || (filter && filter.column && filter.value),
  );

  const applyFilter = (key, value) => {
    if (key === "status") {
      setStatusFilter(value);
      setUnitFilter("");
    }
    if (key === "unit") {
      setUnitFilter(value);
      setStatusFilter("");
    }
    onFilter(value ? key : "", value);
  };

  const resetFilters = () => {
    setStatusFilter("");
    setUnitFilter("");
    onFilter("", "");
  };

  const detailTotal = selectedItem
    ? safeNumber(selectedItem.quantity) * safeNumber(selectedItem.price)
    : 0;
  const canReviewSelected = selectedItem
    ? Number(localStorage.getItem("user_id")) !==
      Number(selectedItem.user_created_id)
    : false;

  return (
    <div className="warehouse-import-modern-page">
      {/* <header className="warehouse-import-header">
        <div>
          <div className="warehouse-import-title-row">
            <h1>Phiếu nhập kho</h1>
            <span>Quản lý</span>
            <i className="simple-icon-arrow-right" />
            <span>Phiếu nhập kho</span>
          </div>
          <p>Quản lý các phiếu nhập hàng, mặt hàng và tình hình nhập kho.</p>
        </div>
        <Button color="primary" onClick={onAdd} disabled={!canCreate}>
          <i className="simple-icon-plus mr-2" />
          Thêm phiếu nhập
        </Button>
      </header> */}

      <div className="warehouse-import-summary-grid">
        <SummaryCard
          label="Tổng nhập"
          value={formatMoney(summary.totalValue)}
          note="Chỉ tính phiếu đã nhập kho"
          icon="iconsminds-receipt-4"
          tone="blue"
        />
        <SummaryCard
          label="Tổng phiếu"
          value={AddCommaNumber(allRows.length)}
          note={`${AddCommaNumber(page.totalItem || allRows.length)} kết quả`}
          icon="iconsminds-file-clipboard"
          tone="purple"
        />
        <SummaryCard
          label="Chờ duyệt"
          value={AddCommaNumber(summary.pending)}
          note="Phiếu đang chờ xử lý"
          icon="simple-icon-clock"
          tone="orange"
        />
        <SummaryCard
          label="Đã duyệt"
          value={AddCommaNumber(summary.approved)}
          note="Phiếu đã cập nhật tồn kho"
          icon="simple-icon-check"
          tone="green"
        />
      </div>

      <div
        className={`warehouse-import-workspace ${selectedItem ? "has-detail" : ""}`}
      >
        <div className="warehouse-import-main-card">
          <div className="warehouse-import-toolbar">
            <div className="warehouse-import-filter-row">
              {/* <div className="warehouse-import-search">
                <i className="simple-icon-magnifier" />
                <Input
                  placeholder="Tìm mã phiếu, mặt hàng, người nhập..."
                  onKeyPress={onSearch}
                />
              </div> */}
              <Input
                type="select"
                value={statusFilter}
                onChange={(event) => applyFilter("status", event.target.value)}
              >
                <option value="">Trạng thái</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Input>
              <Input
                type="select"
                value={unitFilter}
                onChange={(event) => applyFilter("unit", event.target.value)}
              >
                <option value="">Đơn vị tính</option>
                {unitOptions.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </Input>
              {hasActiveFilter && (
                <Button
                  color="warning"
                  outline
                  className="warehouse-import-clear-filter-button"
                  onClick={resetFilters}
                >
                  <i className="simple-icon-close mr-1" />
                  Bỏ lọc đang áp dụng
                </Button>
              )}
              <div className="warehouse-import-toolbar-actions">
                {selectedItems.length > 0 && canDelete &&
                  selectedItems.every((id) => isPending(allRows.find((item) => item.id === id)?.status)) && (
                  <Button color="danger" outline onClick={onDeleteSelected}>
                    <i className="simple-icon-trash mr-1" />
                    Xóa ({selectedItems.length})
                  </Button>
                )}
                <Button
                  color="success"
                  outline
                  onClick={() => exportReceipts(allRows)}
                >
                  <i className="simple-icon-cloud-download mr-1" /> Xuất Excel
                </Button>
                <Button
                  type="button"
                  className="btn-camera-import-trigger"
                  onClick={onCameraImport}
                  disabled={!canCreate}
                  title="Nhập hàng bằng ảnh chụp camera / AI"
                >
                  <i className="simple-icon-camera mr-1" /> Nhập Camera AI
                </Button>
                <Button color="primary" onClick={onAdd} disabled={!canCreate}>
                  <i className="simple-icon-plus mr-1" /> Thêm phiếu nhập
                </Button>
              </div>
            </div>
          </div>

          <div className="warehouse-import-table-wrap">
            <table className="warehouse-import-table">
              <thead>
                <tr>
                  <th className="check-cell" />
                  <th>Mã phiếu</th>
                  <th>Ngày nhập</th>
                  <th>Mặt hàng</th>
                  <th>ĐVT</th>
                  <th className="text-right">Số lượng</th>
                  <th className="text-right">Đơn giá</th>
                  <th className="text-right">Tổng tiền</th>
                  <th>Trạng thái</th>
                  <th>Nhà cung cấp</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => {
                  const checked = selectedItems.includes(item.id);
                  const active = selectedItem && selectedItem.id === item.id;
                  return (
                    <tr
                      key={item.id}
                      className={active ? "is-active" : ""}
                      onClick={() => onSelect(item)}
                    >
                      <td
                        className="check-cell"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <CustomInput
                          type="checkbox"
                          id={`warehouse-import-modern-${item.id}`}
                          checked={checked}
                          onChange={(event) => onToggleSelected(item.id, event)}
                        />
                      </td>
                      <td>
                        <strong>{getReceiptCode(item)}</strong>
                      </td>
                      <td>{item.at || "—"}</td>
                      <td>
                        <span className="warehouse-import-product-name">
                          {item.stock || "—"}
                        </span>
                        {item.stock_barcode ? (
                          <small className="text-muted d-block">Mã vạch: {item.stock_barcode}</small>
                        ) : item.stock_sku ? (
                          <small className="text-muted d-block">Mã: {item.stock_sku}</small>
                        ) : null}
                      </td>
                      <td>{item.unit || "—"}</td>
                      <td className="text-right">
                        {AddCommaNumber(item.quantity || 0)}
                      </td>
                      <td className="text-right">
                        {formatMoney(safeNumber(item.price))}
                      </td>
                      <td className="text-right warehouse-import-total">
                        {formatMoney(
                          safeNumber(item.quantity) * safeNumber(item.price),
                        )}
                      </td>
                      <td>
                        <StatusPill status={item.status} />
                      </td>
                      <td>{item.name || "—"}</td>
                      <td onClick={(event) => event.stopPropagation()}>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            color="link"
                            className="warehouse-import-more-button"
                          >
                            <i className="simple-icon-options-vertical" />
                          </DropdownToggle>
                          <DropdownMenu right>
                            <DropdownItem onClick={() => onSelect(item)}>
                              Xem chi tiết
                            </DropdownItem>
                            {canUpdate && isPending(item.status) && (
                              <DropdownItem onClick={() => onEdit(item)}>
                                Sửa phiếu
                              </DropdownItem>
                            )}
                            {canDelete && isPending(item.status) && (
                              <DropdownItem
                                className="text-danger"
                                onClick={() => onDelete(item)}
                              >
                                Xóa phiếu
                              </DropdownItem>
                            )}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length === 0 && (
              <div className="warehouse-import-empty">
                Chưa có dữ liệu phiếu nhập kho.
              </div>
            )}
          </div>

          <footer className="warehouse-import-pagination-row">
            <div>
              <span>Hiển thị</span>
              <Input
                type="select"
                value={page.selectedPageSize}
                onChange={(event) =>
                  onChangePageSize(Number(event.target.value))
                }
              >
                {page.pageSizes.map((size) => (
                  <option key={size}>{size}</option>
                ))}
              </Input>
              <span>
                {rows.length} / {page.totalItem || rows.length} kết quả
              </span>
            </div>
            <Pagination
              currentPage={page.currentPage}
              totalPage={page.totalPage}
              onChangePage={onChangePage}
            />
          </footer>
        </div>

        <aside className="warehouse-import-detail-panel">
          {selectedItem ? (
            <>
              <div className="warehouse-import-detail-header">
                <div>
                  <div className="warehouse-import-detail-code">
                    <strong>{getReceiptCode(selectedItem)}</strong>
                    <StatusPill status={selectedItem.status} />
                  </div>
                  <small>{selectedItem.at || "Phiếu nhập kho"}</small>
                </div>
                <Button
                  color="link"
                  onClick={() => onSelect(null)}
                  aria-label="Đóng chi tiết"
                >
                  <i className="simple-icon-close" />
                </Button>
              </div>

              <div className="warehouse-import-detail-actions">
                {canUpdate && isPending(selectedItem.status) && (
                  <Button
                    color="primary"
                    outline
                    onClick={() => onEdit(selectedItem)}
                  >
                    <i className="simple-icon-note mr-1" /> Sửa
                  </Button>
                )}
                {isPending(selectedItem.status) && canReviewSelected && (
                  <>
                    <Button
                      color="success"
                      outline
                      onClick={() => onApprove(selectedItem)}
                    >
                      <i className="simple-icon-check mr-1" /> Duyệt
                    </Button>
                    <Button
                      color="danger"
                      outline
                      onClick={() => onReject(selectedItem.id)}
                    >
                      Từ chối
                    </Button>
                  </>
                )}
                {canDelete && isPending(selectedItem.status) && (
                  <Button
                    color="danger"
                    outline
                    onClick={() => onDelete(selectedItem)}
                  >
                    <i className="simple-icon-trash" /> Xóa
                  </Button>
                )}
              </div>

              <nav className="warehouse-import-detail-tabs">
                {[
                  ["info", "Thông tin"],
                  ["items", "Hàng hóa"],
                  ["payment", "Thanh toán"],
                  ["log", "Nhật ký"],
                ].map(([key, label]) => (
                  <button
                    type="button"
                    key={key}
                    className={activeTab === key ? "active" : ""}
                    onClick={() => setActiveTab(key)}
                  >
                    {label}
                  </button>
                ))}
              </nav>

              <div className="warehouse-import-detail-content">
                {activeTab === "info" && (
                  <section className="warehouse-import-detail-card">
                    <h3>Thông tin chung</h3>
                    <dl>
                      <dt>Mã phiếu</dt>
                      <dd>{getReceiptCode(selectedItem)}</dd>
                      <dt>Ngày nhập</dt>
                      <dd>{selectedItem.at || "—"}</dd>
                      <dt>Loại nhập</dt>
                      <dd>Nhập hàng hóa</dd>
                      <dt>Người nhập</dt>
                      <dd>{selectedItem.name || "—"}</dd>
                      <dt>Số điện thoại</dt>
                      <dd>{selectedItem.phone || "—"}</dd>
                      <dt>Địa chỉ</dt>
                      <dd>{selectedItem.address || "—"}</dd>
                      <dt>Người lập</dt>
                      <dd>{getCreatorName(selectedItem)}</dd>
                      <dt>Trạng thái</dt>
                      <dd>{selectedItem.status || "Nháp"}</dd>
                    </dl>
                  </section>
                )}

                {activeTab === "items" && (
                  <section className="warehouse-import-detail-card">
                    <h3>Hàng hóa (1)</h3>
                    <div className="warehouse-import-detail-product">
                      <span className="warehouse-import-product-icon">
                        <i className="iconsminds-box-full" />
                      </span>
                      <div>
                        <strong>
                          {selectedItem.stock || "Chưa có tên hàng"}
                        </strong>
                        <small>
                          {selectedItem.stock_barcode ? `Mã vạch: ${selectedItem.stock_barcode} · ` : selectedItem.stock_sku ? `Mã: ${selectedItem.stock_sku} · ` : ""}
                          {selectedItem.unit || "Chưa có ĐVT"}
                        </small>
                      </div>
                    </div>
                    <dl>
                      <dt>Số lượng</dt>
                      <dd>{AddCommaNumber(selectedItem.quantity || 0)}</dd>
                      <dt>Đơn giá</dt>
                      <dd>{formatMoney(safeNumber(selectedItem.price))}</dd>
                      <dt>Thành tiền</dt>
                      <dd className="text-primary font-weight-bold">
                        {formatMoney(detailTotal)}
                      </dd>
                    </dl>
                  </section>
                )}

                {activeTab === "payment" && (
                  <section className="warehouse-import-detail-card">
                    <h3>Thanh toán</h3>
                    <dl>
                      <dt>Tiền hàng</dt>
                      <dd>{formatMoney(detailTotal)}</dd>
                      <dt>Giảm giá</dt>
                      <dd>0 đ</dd>
                      <dt>Thuế / Phí</dt>
                      <dd>0 đ</dd>
                      <dt>Tổng thanh toán</dt>
                      <dd className="text-primary font-weight-bold">
                        {formatMoney(detailTotal)}
                      </dd>
                    </dl>
                  </section>
                )}

                {activeTab === "log" && (
                  <section className="warehouse-import-detail-card warehouse-import-log-card">
                    <h3>Nhật ký phiếu</h3>
                    <div className="warehouse-import-log-item">
                      <span>
                        <i className="simple-icon-check" />
                      </span>
                      <div>
                        <strong>{selectedItem.status || "Đã tạo phiếu"}</strong>
                        <p>{selectedItem.at || "Chưa có thời gian"}</p>
                        <small>Người lập: {getCreatorName(selectedItem)}</small>
                      </div>
                    </div>
                  </section>
                )}
              </div>
            </>
          ) : (
            <EmptyDetail />
          )}
        </aside>
      </div>
    </div>
  );
};

export default WarehouseImportModernDashboard;
